/**
 * Public Client Portal signup form.
 *
 * Since v1.25 this is a self-serve signup:
 *   1. Prospect picks their own email + password.
 *   2. Owner reviews the request on /access-requests → approves or rejects.
 *   3. On approval, the prospect can log in immediately (2FA enrollment
 *      is still forced on first successful login).
 *
 * No auth required — this is a lead-capture / signup form.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2, Send, Loader2, ArrowLeft, Eye, EyeOff, ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Password policy — kept in sync with backend `_validate_password_strength`.
const PW_RULES = [
  { key: 'length',  label: '12+ characters', test: (p) => p.length >= 12 },
  { key: 'upper',   label: 'Uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower',   label: 'Lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'number',  label: 'Number', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Special character', test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/`~]/.test(p) },
];

// Static "what you'll have access to" catalog — shown on the form so
// prospects know exactly what's on offer BEFORE signing up. Owner
// decides which of these to actually grant on approval.
const ACCESS_INFO = [
  { label: 'Compliance Dashboard',   description: 'Real-time SOC 2 readiness posture and control health' },
  { label: 'SOC 2 Policies',         description: 'All 32 information-security policies, downloadable as PDF' },
  { label: 'CAIQ Responses',         description: 'Cloud Security Alliance questionnaire — 261 answered items' },
  { label: 'Audit Reports',          description: 'Monthly compliance summaries and pentest evidence' },
  { label: 'Accessibility Documents', description: 'VPAT, WCAG 2.1 AA report, and monthly automated scans' },
];


export default function RequestAccessPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [showPw, setShowPw]         = useState(false);
  const [form, setForm] = useState({
    email:         '',
    password:      '',
    password2:     '',
    name:          '',
    company_name:  '',
    reason:        '',
  });

  const pwRuleState = PW_RULES.map((r) => ({ ...r, ok: r.test(form.password) }));
  const pwAllOk     = pwRuleState.every((r) => r.ok);
  const pwMatch     = form.password && form.password === form.password2;
  const canSubmit   =
    form.email && form.name && form.company_name && form.reason.length >= 10
    && pwAllOk && pwMatch;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please complete every field and pick a strong password.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/client/register`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          email:        form.email.trim().toLowerCase(),
          password:     form.password,
          name:         form.name.trim(),
          company_name: form.company_name.trim(),
          reason:       form.reason.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={pageWrap} data-testid="signup-success-page">
        <div style={cardWrap}>
          <div style={{ textAlign: 'center' }} data-testid="access-request-success">
            <CheckCircle2 size={48} color="#059669" style={{ margin: '0 auto' }} />
            <h1 style={{ marginTop: 20, color: '#f8fafc' }}>Thanks — we&apos;ve got your signup</h1>
            <p style={{ color: '#94a3b8', marginTop: 12, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
              Our security team has been notified and will review within one business day.
              You&apos;ll get an email at <b style={{ color: '#e2e8f0' }}>{form.email}</b> once your
              access is approved. Then you can sign in with the password you just picked.
            </p>
            <Link
              to="/trust-center"
              data-testid="back-to-trust-center"
              style={backLink}
            >
              <ArrowLeft size={16} /> Back to Trust Center
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap} data-testid="request-access-page">
      <div style={cardWrap}>
        <Link to="/trust-center" style={crumb} data-testid="crumb-trust-center">
          <ArrowLeft size={14} /> Trust Center
        </Link>

        <h1 style={{ color: '#f8fafc', margin: '18px 0 6px', fontSize: 28 }}>
          Sign up for Client Portal access
        </h1>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: 15, lineHeight: 1.6, maxWidth: 620 }}>
          Please create your account below. Once it&apos;s approved, you&apos;ll be
          able to log in and view every section your account was granted.
          Please note that two-factor authentication is required on first sign-in.
        </p>

        <div style={accessInfoBox} data-testid="access-info-panel">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7dd3fc', fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
            <ShieldCheck size={14} /> What you can be granted access to
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {ACCESS_INFO.map((s) => (
              <li key={s.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <CheckCircle2 size={16} color="#22c55e" style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 13 }}>{s.label}</div>
                  <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 1 }}>{s.description}</div>
                </div>
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 12, color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
            Your Eyvo point-of-contact decides which sections to grant based on your reason for access.
          </div>
        </div>

        <form onSubmit={submit} style={{ marginTop: 24 }} data-testid="access-request-form">
          <div style={twoCol}>
            <Field label="Full name" htmlFor="name">
              <input
                id="name" type="text" required
                data-testid="req-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Company" htmlFor="company">
              <input
                id="company" type="text" required
                data-testid="req-company"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>

          <Field label="Work email" htmlFor="email">
            <input
              id="email" type="email" required
              data-testid="req-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              style={inputStyle}
            />
          </Field>

          <div style={twoCol}>
            <Field label="Choose a password" htmlFor="password">
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  required
                  data-testid="req-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  style={{ ...inputStyle, paddingRight: 38 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  data-testid="req-password-toggle"
                  style={eyeBtn}
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            <Field label="Confirm password" htmlFor="password2">
              <input
                id="password2"
                type={showPw ? 'text' : 'password'}
                required
                data-testid="req-password2"
                value={form.password2}
                onChange={(e) => setForm({ ...form, password2: e.target.value })}
                style={{
                  ...inputStyle,
                  borderColor: form.password2 && !pwMatch ? '#dc2626' : inputStyle.borderColor,
                }}
              />
              {form.password2 && !pwMatch && (
                <div data-testid="req-pw-mismatch" style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>
                  Passwords don&apos;t match
                </div>
              )}
            </Field>
          </div>

          {/* Password rule tick-list */}
          <div style={pwRuleBox} data-testid="req-pw-rules">
            {pwRuleState.map((r) => (
              <div
                key={r.key}
                data-testid={`req-pw-rule-${r.key}-${r.ok ? 'ok' : 'no'}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: r.ok ? '#22c55e' : '#64748b',
                  fontSize: 11,
                }}
              >
                <span style={{
                  width: 14, height: 14, borderRadius: 999,
                  background: r.ok ? 'rgba(34,197,94,0.2)' : 'rgba(100,116,139,0.15)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700,
                }}>{r.ok ? '✓' : '·'}</span>
                {r.label}
              </div>
            ))}
          </div>

          <Field label="Why do you need access?" htmlFor="reason">
            <textarea
              id="reason" required rows={3}
              data-testid="req-reason"
              placeholder="e.g. Vendor security review for Q1 procurement audit."
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            />
          </Field>

          <button
            type="submit"
            disabled={submitting || !canSubmit}
            data-testid="req-submit"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              marginTop: 8,
              padding: '13px 26px', fontSize: 15, fontWeight: 600,
              background: canSubmit ? '#2563eb' : '#334155',
              color: canSubmit ? '#fff' : '#94a3b8',
              border: 'none', borderRadius: 8,
              cursor: (submitting || !canSubmit) ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {submitting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            {submitting ? 'Submitting...' : 'Create account & submit for review'}
          </button>

          <div style={{ color: '#64748b', fontSize: 11, marginTop: 10 }}>
            By signing up you agree to our terms of service. We never share your data.
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── tiny style + field helpers ─────────────────────────────────────
function Field({ label, htmlFor, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={htmlFor} style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

const pageWrap = {
  minHeight: '100vh', background: '#0f172a',
  padding: '40px 20px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  color: '#e2e8f0',
};

const cardWrap = {
  maxWidth: 640, margin: '0 auto',
  background: '#1e293b',
  padding: 32,
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.08)',
};

const crumb = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  color: '#94a3b8', textDecoration: 'none', fontSize: 13,
};

const backLink = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  color: '#60a5fa', textDecoration: 'none', fontSize: 14, marginTop: 24,
};

const labelStyle = {
  display: 'block',
  color: '#cbd5e1',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: 'rgba(15,23,42,0.6)',
  color: '#f1f5f9',
  border: '1px solid rgba(148,163,184,0.2)',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const twoCol = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 14,
};

const eyeBtn = {
  position: 'absolute',
  right: 8, top: '50%', transform: 'translateY(-50%)',
  background: 'transparent', border: 'none',
  color: '#94a3b8', cursor: 'pointer',
  padding: 6, display: 'inline-flex',
};

const pwRuleBox = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 6,
  padding: 12,
  background: 'rgba(15,23,42,0.6)',
  borderRadius: 6,
  border: '1px solid rgba(148,163,184,0.1)',
  marginBottom: 14,
};

const accessInfoBox = {
  marginTop: 22,
  padding: 18,
  background: 'rgba(59,130,246,0.06)',
  border: '1px solid rgba(59,130,246,0.20)',
  borderRadius: 10,
};
